"use client";
import { useLazyQuery } from "@apollo/client/react";
import { cloneDeep } from "es-toolkit";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { use } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import type { PostType } from "@/contexts/feed-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import {
  type Post,
  PostByPostHashCommentsDocument,
  type PostByPostHashCommentsQuery,
  PostsOrderBy,
} from "@/graphql/codegen/graphql";
import { getNonBlockFilter } from "@/hooks-api/use-get-posts";
import type { Dictionary } from "@/utils/helpers";
import { isAbortError, update } from "@/utils/helpers";

interface CommentsContextProps {
  data: PostByPostHashCommentsQuery | null;
  OPCommentsData: PostByPostHashCommentsQuery | null;
  loading: boolean;
  onCommentCreated: (comment: Post) => void;
  loadMoreRepliesForComment: (commentPostHash: string) => Promise<void>;
  setOrderByPreset: Dispatch<SetStateAction<keyof typeof orderByPresets>>;
  orderByPreset: keyof typeof orderByPresets;
  orderByPresets: Dictionary<{
    label: string;
    opCommentsFilter: PostsOrderBy[];
    commentsFilter: PostsOrderBy[];
  }>;
  reset: () => void;
  paginationState: PaginationState;
  updateComment: (hash: string, comment: PostType) => void;
}

const orderByPresets = {
  wealth: {
    label: "Upvote Wealth",
    opCommentsFilter: [
      PostsOrderBy.UpvoteCappedWealthUsdCentsDesc,
      PostsOrderBy.TimestampAsc,
    ],
    commentsFilter: [PostsOrderBy.UpvoteCappedWealthUsdCentsDesc],
  },
  latest: {
    label: "Latest",
    opCommentsFilter: [
      PostsOrderBy.TimestampDesc,
      PostsOrderBy.UpvoteCappedWealthUsdCentsDesc,
    ],
    commentsFilter: [
      PostsOrderBy.TimestampDesc,
      PostsOrderBy.UpvoteCappedWealthUsdCentsDesc,
    ],
  },
  diamonds: {
    label: "Diamonds",
    opCommentsFilter: [
      PostsOrderBy.TotalDiamondValueUsdCentsDesc,
      PostsOrderBy.TimestampDesc,
    ],
    commentsFilter: [
      PostsOrderBy.TotalDiamondValueUsdCentsDesc,
      PostsOrderBy.TimestampDesc,
    ],
  },
};

const CommentsContext = createContext<CommentsContextProps | undefined>(
  undefined,
);

interface CommentsProviderProps {
  children: ReactNode;
  postCreatorPublicKey: string;
}

const LIMIT = 10;

type PaginationState = Dictionary<{
  offset: number;
  first: number;
  hasMore: boolean;
}>;

export const CommentsProvider = ({
  children,
  postCreatorPublicKey,
}: CommentsProviderProps) => {
  const [paginationState, setPaginationState] = useState<PaginationState>({});
  const [commentsData, setCommentsData] =
    useState<PostByPostHashCommentsQuery | null>(null);
  const [OPCommentsData, setOPCommentsData] =
    useState<PostByPostHashCommentsQuery | null>(null);
  //const [loadComments] = useLazyQuery(PostByPostHashCommentsCachedDocument);
  const [loadComments] = useLazyQuery(PostByPostHashCommentsDocument, {
    fetchPolicy: "network-only",
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const requestVersionRef = useRef(0);
  const { toast } = useToast();
  const [orderByPreset, setOrderByPreset] =
    useState<CommentsContextProps["orderByPreset"]>("wealth");

  const { post, isPureRepost, rootPostHash } = usePostContext();
  const { currentUserPublicKey } = use(UserContext);
  const postHash = isPureRepost ? post.repostedPost?.postHash : post.postHash;

  // Get block filter for excluding blocked users (both directions)
  const NON_BLOCKED_FILTER = useMemo(
    () => (currentUserPublicKey ? getNonBlockFilter(currentUserPublicKey) : {}),
    [currentUserPublicKey],
  );

  const updateComment = useCallback(
    (hash: string, comment: PostType) => {
      const commentDataSetter: SetStateAction<
        PostByPostHashCommentsQuery | null
      > = (prevData) => {
        if (!prevData?.postByPostHash) return prevData;

        const updatedData = cloneDeep(prevData);

        const updateCommentInPost = (post: Post): boolean => {
          if (post.postHash === hash) {
            Object.assign(post, comment);
            return true;
          }

          return (
            post.replies?.nodes?.filter(Boolean).some(updateCommentInPost) ||
            false
          );
        };

        updateCommentInPost(updatedData.postByPostHash as Post);

        return updatedData;
      };

      if (postCreatorPublicKey === comment.poster?.publicKey) {
        setOPCommentsData(commentDataSetter);
      } else {
        setCommentsData(commentDataSetter);
      }
    },
    [postCreatorPublicKey],
  );

  const onCommentCreated = useCallback(
    (comment: Post) => {
      const parentPostHash = comment.parentPost?.postHash;

      const commentDataSetter: SetStateAction<
        PostByPostHashCommentsQuery | null
      > = (prevData) => {
        if (!prevData?.postByPostHash) return prevData;

        const updatedData = cloneDeep(prevData);

        const insertCommentIntoPost = (post: Post): boolean => {
          if (post.postHash === parentPostHash) {
            if (!post.replies) {
              // @ts-expect-error note
              post.replies = { nodes: [] as Post[], totalCount: 0 };
            }
            post.replies.nodes.unshift(comment);
            post.replies.totalCount = (post.replies.totalCount || 0) + 1;
            return true;
          }

          return (
            post.replies?.nodes?.filter(Boolean).some(insertCommentIntoPost) ||
            false
          );
        };

        insertCommentIntoPost(updatedData.postByPostHash as Post);

        return updatedData;
      };
      // Note: we need to do both because we have two separate queries for comments

      if (
        postCreatorPublicKey === comment.poster?.publicKey &&
        comment.parentPost?.postHash === postHash
      ) {
        setOPCommentsData(commentDataSetter);
      } else {
        if (comment.parentPost?.postHash !== postHash) {
          setOPCommentsData(commentDataSetter);
        }
        setCommentsData(commentDataSetter);
      }
    },
    [postCreatorPublicKey, postHash],
  );
  const loadMoreRepliesForComment = useCallback(
    async (commentPostHash: string) => {
      if (!postHash) return;
      const { first, offset } = paginationState[commentPostHash] || {
        offset: 0,
        first: LIMIT,
        hasMore: true,
      };
      const requestVersion = requestVersionRef.current;

      const request = loadComments({
        variables: {
          offset: offset,
          first,
          postHash: commentPostHash,
          orderBy: orderByPresets[orderByPreset].commentsFilter,
          filter: {
            isHidden: {
              equalTo: false,
            },
            poster: {
              publicKey: {
                notEqualTo: postCreatorPublicKey,
              },
              ...NON_BLOCKED_FILTER,
            },
            rootPostHash: {
              equalTo: rootPostHash,
            },
          },
          filterNested: {
            isHidden: {
              equalTo: false,
            },
            rootPostHash: {
              equalTo: rootPostHash,
            },
          },
        },
      });
      request.retain?.();
      const { data } = await request;

      if (requestVersion !== requestVersionRef.current) {
        return;
      }

      const updatedData = cloneDeep(commentsData);
      const newComments = data?.postByPostHash?.replies?.nodes ?? [];
      const newCommentsTotalCount =
        data?.postByPostHash?.replies?.totalCount ?? 0;
      const commentPath = findCommentPath(
        postHash,
        updatedData?.postByPostHash?.replies?.nodes.filter(Boolean) ?? [],
        commentPostHash,
      );

      if (commentPath && updatedData) {
        update(updatedData, commentPath, (comment) => {
          return {
            ...comment,
            replies: {
              ...comment.replies,
              nodes: [...comment.replies.nodes, ...newComments],
              totalCount: newCommentsTotalCount,
            },
          };
        });

        setCommentsData(updatedData);

        setPaginationState({
          ...paginationState,
          [commentPostHash]: {
            offset: offset + first,
            first,
            hasMore: newComments.length === LIMIT,
          },
        });
      }
    },
    [
      commentsData,
      loadComments,
      orderByPreset,
      paginationState,
      postCreatorPublicKey,
      postHash,
      rootPostHash,
      NON_BLOCKED_FILTER,
    ],
  );

  useEffect(() => {
    const run = async () => {
      const requestVersion = requestVersionRef.current;
      try {
        if (!rootPostHash || !postHash) return;

        setInitialLoading(true);
        const commentsRequest = loadComments({
          variables: {
            offset: 0,
            first: LIMIT,
            postHash,
            orderBy: orderByPresets[orderByPreset].commentsFilter,
            filter: {
              poster: {
                publicKey: {
                  notEqualTo: postCreatorPublicKey,
                },
                ...NON_BLOCKED_FILTER,
              },
              rootPostHash: {
                equalTo: rootPostHash,
              },
            },
            filterNested: {
              rootPostHash: {
                equalTo: rootPostHash,
              },
            },
          },
        });
        commentsRequest.retain?.();
        const { data } = await commentsRequest;

        if (requestVersion !== requestVersionRef.current) {
          return;
        }

        const opCommentsRequest = loadComments({
          variables: {
            postHash,
            orderBy: orderByPresets[orderByPreset].opCommentsFilter,
            filter: {
              poster: {
                publicKey: {
                  equalTo: postCreatorPublicKey,
                },
                ...NON_BLOCKED_FILTER,
              },
              rootPostHash: {
                equalTo: rootPostHash,
              },
            },
            filterNested: {
              rootPostHash: {
                equalTo: rootPostHash,
              },
            },
          },
        });
        opCommentsRequest.retain?.();
        const { data: opData } = await opCommentsRequest;

        if (requestVersion !== requestVersionRef.current) {
          return;
        }

        if (data) {
          setCommentsData(data);
        }
        if (opData) {
          setOPCommentsData(opData);
        }

        setPaginationState((prev) => {
          return {
            ...prev,
            [postHash]: {
              offset: LIMIT,
              first: LIMIT,
              hasMore: data?.postByPostHash?.replies?.nodes?.length === LIMIT,
            },
          };
        });
      } catch (e) {
        if (!isAbortError(e)) {
          toast({
            variant: "destructive",
            title: "Error",
            description: `An error happened when fetching comments`,
            details: createToastErrorDetails(e),
          });
        }
      } finally {
        setInitialLoading(false);
      }
    };

    if (postHash && !commentsData) {
      run();
    }
  }, [
    rootPostHash,
    commentsData,
    loadComments,
    orderByPreset,
    postCreatorPublicKey,
    postHash,
    toast,
    NON_BLOCKED_FILTER,
  ]);

  const reset = useCallback(() => {
    requestVersionRef.current += 1;
    setPaginationState({});
    setCommentsData(null);
    setOPCommentsData(null);
    setInitialLoading(true);
  }, []);

  return (
    <CommentsContext.Provider
      value={{
        data: commentsData,
        updateComment,
        OPCommentsData,
        loading: initialLoading,
        onCommentCreated,
        loadMoreRepliesForComment,
        orderByPreset,
        setOrderByPreset,
        orderByPresets,
        reset,
        paginationState,
      }}
    >
      {children}
    </CommentsContext.Provider>
  );
};

export const useCommentsContext = (): CommentsContextProps => {
  const context = useContext(CommentsContext);
  if (context === undefined) {
    throw new Error("useComments must be used within a CommentsProvider");
  }
  return context;
};

function findCommentPath(
  rootPostHash: string,
  nodes: PostType[],
  commentPostHash: string,
  basePath: string = "postByPostHash",
): string | null {
  if (rootPostHash === commentPostHash) return basePath;

  if (!nodes?.length) return null;

  for (let i = 0; i < nodes.length; i++) {
    const path = `${basePath}.replies.nodes.${i}`;
    if (nodes[i].postHash === commentPostHash) {
      return path;
    }
    if (nodes[i].replies && (nodes[i].replies?.nodes?.length || 0) > 0) {
      const innerComments = nodes[i]?.replies?.nodes ?? [];

      const commentPath = findCommentPath(
        rootPostHash,
        innerComments as Post[],
        commentPostHash,
        path,
      );
      if (commentPath) return commentPath;
    }
  }
  return null;
}
